import {trackEventWithModule} from 'analytics/Analytics';
import {CompletedTimedEventMetadata} from 'analytics/metadata/CompletedTimedEventMetadata';
import {MigrationTab} from 'config-migration/types/MigrationTab';
import {
  ActionOrigin,
  ANALYTICS_MODULE,
  CommonAnalyticsActions,
  COMMON_ANALYTICS_ACTIONS,
  ConfigMigrationAnalyticsAction,
  CONFIG_MIGRATION_ANALYTICS_ACTIONS,
} from 'constants/AnalyticsConstants';

// -------------------
// Shared Metadata
// -------------------
/**
 * Basic metadata for any ConfigMigration-related analytics event.
 *
 * Note: Property names should be in snake_case as per Analytics Instrument Best Practices.
 */
export interface BaseConfigMigrationEventMetadata {
  /** Where on the page the action occurred, see Analytics Instrumentation Best Practices */
  action_origin: ActionOrigin;

  /**
   * The shared id for a group of events (roughly within one session/set of actions), as described in
   * Product-Tracking-Calls-How-to-Instrument
   */
  event_group_id: string;

  /** The organization schemas/objects are being exported out of */
  source_organization_id?: string;

  /** The organization schemas/objects are being imported into */
  destination_organization_id?: string;
}

/** Metadata for events that are have a duration and have their completion tracked separately. */
export type CompletedTimedConfigMigrationEventMetadata = BaseConfigMigrationEventMetadata &
  CompletedTimedEventMetadata;

export function getActionOrigin(tab: MigrationTab): ActionOrigin {
  switch (tab) {
    case MigrationTab.EXPORT:
      return ActionOrigin.CONFIG_MIGRATION_EXPORT_TAB;
    case MigrationTab.IMPORT:
      return ActionOrigin.CONFIG_MIGRATION_IMPORT_TAB;
    case MigrationTab.MANAGEMENT:
      return ActionOrigin.CONFIG_MIGRATION_MANAGEMENT_HISTORY_TABLE;
    default:
      return ActionOrigin.CONFIG_MIGRATION;
  }
}

// -------------------
// Shared Tracking Functions
// -------------------
/**
 * Helper to log ConfigMigration specific events and actions with
 *  metadata extending `BaseConfigMigrationEventMetadata`
 *
 * Available actions defined in client/constants/analytics-products/ConfigMigrationAnalyticsConstants.ts
 */
export function trackConfigMigrationEvent(
  actionName: ConfigMigrationAnalyticsAction,
  metadata: BaseConfigMigrationEventMetadata
): void {
  trackEventWithModule(
    CONFIG_MIGRATION_ANALYTICS_ACTIONS[actionName],
    ANALYTICS_MODULE.CONFIG_MIGRATION,
    metadata
  );
}

/**
 * Helper to log Common Analytics events events and actions scoped to ConfigMigration using
 *  metadata extending `BaseConfigMigrationEventMetadata`
 *
 * Available actions defined in client/constants/AnalyticsConstants.ts
 */
export function trackCommonEvent(
  actionName: CommonAnalyticsActions,
  metadata: BaseConfigMigrationEventMetadata
): void {
  trackEventWithModule(COMMON_ANALYTICS_ACTIONS[actionName], ANALYTICS_MODULE.CONFIG_MIGRATION, metadata);
}
